import { useState } from 'react';
import {
  List,
  TextField,
  FunctionField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  useGetOne,
  useGetList,
  Button,
  TopToolbar,
} from 'react-admin';
import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  Divider,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { formatVnd } from '../utils/format';
import { bankNameByBin, BANKS } from '../services/vietqr';
import { buildApprovedPaymentOrder, canRegeneratePaymentQr, rebuildApprovedPaymentOrderQr } from '../services/paymentApproval';
import { exportPaymentOrders } from '../services/excelExport';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveDataList } from '../components/ResponsiveDataList';
import { StatRow } from '../components/StatCards';
import { formPageSx, pageShellSx, resourceListSx } from '../ui';
import type { PaymentOrder, DebtSlip, VatInvoice } from '../types';

const bankChoices = BANKS.map((b) => ({ id: b.bin, name: `${b.name} (${b.bin})` }));

const STATUS: Record<string, { label: string; color: any }> = {
  draft: { label: 'Nháp', color: 'default' },
  pending: { label: 'Chờ duyệt', color: 'warning' },
  approved: { label: 'Đã duyệt', color: 'success' },
  rejected: { label: 'Từ chối', color: 'error' },
};

const StatusChip = () => {
  const r = useRecordContext<PaymentOrder>();
  if (!r) return null;
  const s = STATUS[r.status] || STATUS.draft;
  return <Chip size="small" label={s.label} color={s.color} variant={r.status === 'draft' ? 'outlined' : 'filled'} />;
};

const orderFilters = [
  <SelectInput
    key="status"
    source="status"
    label="Trạng thái"
    choices={Object.entries(STATUS).map(([id, v]) => ({ id, name: v.label }))}
    alwaysOn
  />,
];

const ExportApprovedButton = () => {
  const dp = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const { data } = await dp.getList<PaymentOrder>('paymentOrders', {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'approvedAt', order: 'DESC' },
        filter: { status: 'approved' },
      });
      if (!data.length) {
        notify('Chưa có lệnh chi nào ở trạng thái "Đã duyệt".', { type: 'warning' });
        return;
      }
      exportPaymentOrders(data);
      notify(`Đã xuất ${data.length} lệnh đã duyệt ra Excel`, { type: 'success' });
    } catch {
      notify('Không thể xuất tệp Excel. Vui lòng thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  return <Button label="Xuất lệnh đã duyệt (Excel)" onClick={onClick} disabled={loading} startIcon={<DownloadRoundedIcon />} />;
};

const PaymentStats = () => {
  const { data = [] } = useGetList<PaymentOrder>('paymentOrders', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'createdAt', order: 'DESC' },
  });
  const by = (s: string) => data.filter((o) => o.status === s);
  const sum = (arr: PaymentOrder[]) => arr.reduce((a, o) => a + (o.amount || 0), 0);
  const pending = by('pending');
  const approved = by('approved');
  const rejected = by('rejected');
  return (
    <StatRow
      stats={[
        { label: 'Chờ duyệt', value: pending.length, sub: formatVnd(sum(pending)), icon: <HourglassTopRoundedIcon />, color: '#F59E0B' },
        { label: 'Đã duyệt', value: approved.length, sub: formatVnd(sum(approved)), icon: <VerifiedRoundedIcon />, color: '#0FAF78' },
        { label: 'Từ chối', value: rejected.length, sub: formatVnd(sum(rejected)), icon: <HighlightOffRoundedIcon />, color: '#E94B5F' },
        { label: 'Tổng lệnh chi', value: data.length, sub: formatVnd(sum(data)), icon: <SavingsRoundedIcon />, color: '#1D4E6D' },
      ]}
    />
  );
};

export const PaymentOrderList = () => (
  <Box sx={pageShellSx}>
    <PageHeader
      title="Lệnh chi"
      subtitle="Theo dõi lệnh thanh toán, trạng thái phê duyệt và thông tin VietQR."
      icon={<PaymentsRoundedIcon />}
      actions={<ExportApprovedButton />}
    />
    <PaymentStats />
    <List
      sx={resourceListSx}
      actions={false}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      title="Lệnh chi"
      filters={orderFilters}
      empty={false}
    >
      <ResponsiveDataList<PaymentOrder>
        ariaLabel="Danh sách lệnh chi"
        primaryText={(r) => r.supplierName || '(chưa có nhà cung cấp)'}
        secondaryText={(r) => `${formatVnd(r.amount)} · ${bankNameByBin(r.bankBin) || 'Chưa rõ ngân hàng'}${r.accountNumber ? ` ${r.accountNumber}` : ''}`}
        tertiaryText={(r) => STATUS[r.status]?.label || r.status}
        leftIcon={() => <PaymentsRoundedIcon fontSize="small" />}
      >
        <TextField source="supplierName" label="Nhà cung cấp" />
        <FunctionField
          label="Số tiền"
          textAlign="right"
          render={(r: PaymentOrder) => <Typography variant="body2" sx={{ fontWeight: 850, textAlign: 'right' }}>{formatVnd(r.amount)}</Typography>}
        />
        <FunctionField
          label="Ngân hàng"
          render={(r: PaymentOrder) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <AccountBalanceRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2">{`${bankNameByBin(r.bankBin)} ${r.accountNumber || ''}`.trim()}</Typography>
            </Box>
          )}
        />
        <FunctionField label="Trạng thái" render={() => <StatusChip />} />
        <FunctionField
          label="Tạo lúc"
          render={(r: PaymentOrder) => (r.createdAt ? new Date(r.createdAt).toLocaleString('vi-VN') : '')}
        />
        <EditButton label="Sửa" />
      </ResponsiveDataList>
    </List>
  </Box>
);

const SlipPanel = () => {
  const r = useRecordContext<PaymentOrder>();
  const { data: slip } = useGetOne<DebtSlip>(
    'debtSlips',
    { id: r?.debtSlipId! },
    { enabled: !!r?.debtSlipId },
  );
  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLongRoundedIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>1 · Đơn nợ gốc</Typography>
      </Box>
      <CardContent>
        {slip?.imageDataUrl ? (
          <Box component="img" src={slip.imageDataUrl} alt="Đơn nợ" sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: '12px', bgcolor: '#F3F6F7', border: '1px solid', borderColor: 'divider' }} />
        ) : (
          <Typography variant="caption">Không có ảnh</Typography>
        )}
        {slip && (
          <Box sx={{ mt: 1.5, p: 1.4, borderRadius: '12px', bgcolor: 'rgba(14,116,144,.05)' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Tổng phải trả</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>{formatVnd(slip.totalPayable)}</Typography>
            <Typography variant="caption" color="text.secondary">Đơn lần này: {formatVnd(slip.currentAmount)}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const InvoicePanel = () => {
  const r = useRecordContext<PaymentOrder>();
  const { data: inv } = useGetOne<VatInvoice>(
    'vatInvoices',
    { id: r?.vatInvoiceId! },
    { enabled: !!r?.vatInvoiceId },
  );
  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <VerifiedRoundedIcon color="success" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>2 · Hóa đơn VAT khớp</Typography>
      </Box>
      <CardContent>
        {inv ? (
          <Box sx={{ display: 'grid', gap: 1.1 }}>
            <Box><Typography variant="caption" color="text.secondary">Số HĐ</Typography><Typography variant="body2" sx={{ fontWeight: 800 }}>{inv.invoiceNumber}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Nhà cung cấp</Typography><Typography variant="body2">{inv.supplierName}</Typography></Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box><Typography variant="caption" color="text.secondary">MST</Typography><Typography variant="body2">{inv.taxCode || '—'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Ngày</Typography><Typography variant="body2">{inv.invoiceDate}</Typography></Box>
            </Box>
            <Box sx={{ p: 1.4, borderRadius: '12px', bgcolor: 'rgba(15,175,120,.07)' }}>
              <Typography variant="caption" color="text.secondary">Số tiền thanh toán</Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 850 }}>{formatVnd(inv.amount)}</Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="caption">Chưa liên kết HĐ VAT</Typography>
        )}
      </CardContent>
    </Card>
  );
};

const QrPanel = () => {
  const r = useRecordContext<PaymentOrder>();
  const notify = useNotify();
  if (!r) return null;
  const copy = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      notify('Đã sao chép số tài khoản', { type: 'info' });
    } catch {
      notify('Trình duyệt không cho sao chép tự động', { type: 'warning' });
    }
  };
  return (
    <Card sx={{ height: '100%', overflow: 'hidden', borderColor: 'rgba(14,116,144,.22)', background: 'linear-gradient(180deg, rgba(14,116,144,.035), rgba(255,255,255,1))' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCode2RoundedIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>3 · VietQR thanh toán</Typography>
      </Box>
      <CardContent sx={{ textAlign: 'center' }}>
        {r.status === 'approved' && r.vietQrUrl ? (
          <>
            <Box component="img" src={r.vietQrUrl} alt="VietQR" sx={{ width: '100%', maxWidth: 260, maxHeight: 300, objectFit: 'contain', bgcolor: '#fff', p: 1, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }} />
            <Typography variant="h6" color="primary" sx={{ mt: 1.2, fontWeight: 850 }}>{formatVnd(r.amount)}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{bankNameByBin(r.bankBin)} — {r.accountNumber}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>{r.accountHolder}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>ND: {r.addInfo}</Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <MuiButton size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={() => copy(r.accountNumber)}>Sao chép STK</MuiButton>
              <MuiButton size="small" variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={() => window.open(r.vietQrUrl, '_blank', 'noopener')}>Mở/Tải QR</MuiButton>
            </Box>
          </>
        ) : r.status === 'rejected' ? (
          <Alert severity="error">Lệnh đã bị từ chối nên không có VietQR.</Alert>
        ) : (
          <Alert severity="info">VietQR sẽ được tạo sau khi lệnh được duyệt.</Alert>
        )}
      </CardContent>
    </Card>
  );
};

const ApprovalBar = () => {
  const r = useRecordContext<PaymentOrder>();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!r) return null;

  if (r.status === 'approved') {
    return <Alert severity="success" sx={{ mb: 2 }}>Đã duyệt lúc {r.approvedAt && new Date(r.approvedAt).toLocaleString('vi-VN')}. Có thể quét QR để chuyển tiền.</Alert>;
  }
  if (r.status === 'rejected') {
    return <Alert severity="error" sx={{ mb: 2 }}>Đã từ chối. Lý do: {r.rejectionReason || '—'}</Alert>;
  }
  const approve = async () => {
    setBusy(true);
    try {
      const approved = buildApprovedPaymentOrder(r);
      await dp.update('paymentOrders', {
        id: r.id,
        data: approved,
        previousData: r,
      });
      notify('Đã duyệt lệnh chi và tạo VietQR.', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(String(error?.message || 'Không thể tạo VietQR; lệnh vẫn ở trạng thái chờ duyệt.'), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    await dp.update('paymentOrders', {
      id: r.id,
      data: { ...r, status: 'rejected', rejectionReason: reason, approvedAt: new Date().toISOString(), vietQrUrl: undefined },
      previousData: r,
    });
    notify('Đã từ chối lệnh chi', { type: 'info' });
    refresh();
  };

  return (
    <Card sx={{ mb: 2, borderColor: 'rgba(245,158,11,.25)', bgcolor: 'rgba(245,158,11,.045)' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 850, mb: 1.2 }}>Phê duyệt lệnh chi</Typography>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap' }}>
          <MuiButton variant="contained" color="success" startIcon={<CheckCircleRoundedIcon />} onClick={approve} disabled={busy}>{busy ? 'Đang tạo QR…' : 'Duyệt lệnh'}</MuiButton>
          <MuiTextField size="small" label="Lý do từ chối" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 260 }, flex: 1 }} />
          <MuiButton variant="outlined" color="error" startIcon={<CancelRoundedIcon />} onClick={reject}>Từ chối</MuiButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const RegenerateQrButton = () => {
  const r = useRecordContext<PaymentOrder>();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  if (!r || !canRegeneratePaymentQr(r)) return null;
  const onClick = async () => {
    try {
      const updated = rebuildApprovedPaymentOrderQr(r);
      await dp.update('paymentOrders', { id: r.id, data: updated, previousData: r });
      notify('Đã tạo lại VietQR', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(String(error?.message || 'Không thể tạo lại VietQR.'), { type: 'error' });
    }
  };
  return <Button label="Tạo lại QR" onClick={onClick} startIcon={<QrCode2RoundedIcon />} />;
};

const EditActions = () => (
  <TopToolbar sx={{ p: 0, minHeight: 0 }}>
    <RegenerateQrButton />
  </TopToolbar>
);

export const PaymentOrderEdit = () => (
  <Box sx={pageShellSx}>
    <PageHeader
      title="Chi tiết lệnh chi"
      subtitle="Kiểm tra chứng từ, thông tin thanh toán, VietQR và trạng thái phê duyệt."
      icon={<PaymentsRoundedIcon />}
    />
    <Edit title="Lệnh chi" actions={<EditActions />} sx={formPageSx}>
      <SimpleForm>
        <Box sx={{ width: '100%' }}>
          <ApprovalBar />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 2 }}>
            <SlipPanel />
            <InvoicePanel />
            <QrPanel />
          </Box>
          <Divider sx={{ my: 2.4 }} />
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 850 }}>Thông tin chuyển khoản</Typography>
            <Typography variant="body2" color="text.secondary">Kiểm tra thông tin ngân hàng trước khi duyệt; QR chỉ hiển thị sau khi duyệt thành công.</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            <TextInput source="supplierName" label="Nhà cung cấp" fullWidth sx={{ gridColumn: { md: '1 / -1' } }} />
            <NumberInput source="amount" label="Số tiền" fullWidth />
            <SelectInput source="bankBin" label="Ngân hàng" choices={bankChoices} fullWidth />
            <TextInput source="accountNumber" label="Số tài khoản" fullWidth />
            <TextInput source="accountHolder" label="Chủ tài khoản" fullWidth />
            <TextInput source="addInfo" label="Nội dung chuyển khoản" fullWidth sx={{ gridColumn: { md: '1 / -1' } }} />
          </Box>
        </Box>
      </SimpleForm>
    </Edit>
  </Box>
);
